import { useState, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Check, ChevronDown, Loader2, ArrowLeft, Globe } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { cn } from '@/lib/utils';
import { useGetCustomLlmModels, useGetLlmModels } from '@/modules/gpt-chats/hooks/use-gpt-chat';
import { formatProviderName, getProviderConfig } from '../../utils/model-selector';
import { SelectModelType } from '../../types/chat-store.types';
import { useTranslation } from 'react-i18next';
import chLogo from '@/assets/logos/ch-logo.svg';
import euLogo from '@/assets/logos/eu-logo.svg';
import { useGetAgents } from '../../hooks/use-agents';

type GroupModel = {
  provider: string;
  label: string;
  isBlocksModels: boolean;
  isAgents?: boolean;
  models: {
    model: string;
    label: string;
    type: string;
    widget_id?: string;
    region?: string | null;
  }[];
};

const REGION_LOGOS: Record<string, string> = {
  ch: chLogo,
  eu: euLogo,
};

const RegionLogo = ({ region }: { region?: string | null }) => {
  if (!region) return null;

  if (region === 'global') {
    return <Globe className="h-6 w-6 text-muted-foreground" />;
  }

  if (!REGION_LOGOS[region]) return null;

  return (
    <img
      src={REGION_LOGOS[region]}
      alt={region}
      className="h-6 w-6 aspect-square object-cover overflow-hidden"
    />
  );
};

interface GroupedModelSelectorProps {
  value?: SelectModelType;
  onChange?: (value: SelectModelType) => void;
  locked?: boolean;
  isAgentChat?: boolean;
}

interface GroupedModelFormSelectorProps {
  form: UseFormReturn<any>;
  modelType?: 'chat' | 'embedding';
  isRequired?: boolean;
}

const GroupedModelFormSelector = ({
  form,
  modelType = 'chat',
  isRequired = false,
}: GroupedModelFormSelectorProps) => {
  const { data: blocksModels, isLoading } = useGetLlmModels();

  const models = useMemo(
    () => (blocksModels || []).filter((model) => model.model_type === modelType),
    [blocksModels, modelType]
  );

  return (
    <FormField
      control={form.control}
      name="llm_config.model_name"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-sm font-medium">
            Model{isRequired && <span className="ml-1 text-red-500">*</span>}
          </FormLabel>
          <FormControl>
            <Select
              value={field.value}
              disabled={isLoading}
              onValueChange={(value) => {
                const selected = models.find((model) => model.model_name === value);
                field.onChange(value);
                form.setValue('llm_config.model_name', value, { shouldValidate: true });
                form.setValue('llm_config.provider', selected?.provider || '', {
                  shouldValidate: true,
                });
                form.setValue('llm_config.is_blocks_provider', true, { shouldValidate: true });
                form.setValue('llm_config.model_id', undefined, { shouldValidate: false });
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={isLoading ? 'Loading models...' : 'Select model'} />
              </SelectTrigger>
              <SelectContent>
                {models.map((model) => (
                  <SelectItem
                    key={`${model.provider}-${model.model_name}-${model.region || 'global'}`}
                    value={model.model_name}
                  >
                    {model.model_name_label || model.model_name}
                    {model.provider_label ? ` (${model.provider_label})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default GroupedModelFormSelector;

export const GroupedModelSelector = ({
  value,
  onChange,
  locked = false,
  isAgentChat = false,
}: GroupedModelSelectorProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const [selectedProvider, setSelectedProvider] = useState<GroupModel | null>(null);
  const [mobileView, setMobileView] = useState<'providers' | 'models'>('providers');

  const {
    data: customModels,
    isLoading: isLoadingCustom,
    error: customError,
  } = useGetCustomLlmModels();
  const { data: blocksModels, isLoading: isLoadingBlocks, error: blocksError } = useGetLlmModels();
  const { data: agentsData } = useGetAgents({
    limit: 100,
    offset: 0,
    project_key: import.meta.env.VITE_X_BLOCKS_KEY,
  });

  const isLoading = isLoadingCustom || isLoadingBlocks;
  const hasError = customError && blocksError;

  const groupedModels = useMemo(() => {
    const allModels: Record<string, GroupModel> = {};

    if (!customModels && !blocksModels) return allModels;

    blocksModels?.forEach((model) => {
      if (model.model_type !== 'chat') return;
      const provider = model.provider.toLowerCase();
      if (!allModels[provider]) {
        allModels[provider] = {
          provider: provider,
          label: formatProviderName(model.provider_label || model.provider),
          isBlocksModels: true,
          isAgents: false,
          models: [],
        };
      }
      allModels[provider].models.push({
        model: model.model_name,
        label: model.model_name_label,
        type: model.model_type,
        region: model.region,
      });
    });

    customModels?.models?.forEach((model) => {
      const provider = model.Provider.toLowerCase();
      if (!allModels[provider]) {
        allModels[provider] = {
          provider: provider,
          label: formatProviderName(model.Provider),
          isBlocksModels: false,
          isAgents: false,
          models: [],
        };
      }
      allModels[provider].models.push({
        model: model._id,
        label: model.ModelName,
        type: model.ModelType,
      });
    });

    if (agentsData?.agents && agentsData.agents.length > 0) {
      allModels['agents'] = {
        provider: 'agents',
        label: 'Agents',
        isBlocksModels: false,
        isAgents: true,
        models: agentsData.agents.map((agent: any) => ({
          model: agent.agent_key || agent.id,
          label: agent.agent_name || agent.name,
          type: 'agent',
          widget_id: agent.widget_id,
        })),
      };
    }

    return allModels;
  }, [customModels, blocksModels, agentsData]);

  const filteredModels = useMemo(() => {
    if (isAgentChat) return groupedModels;

    const filtered = { ...groupedModels };
    delete filtered['agents'];
    return filtered;
  }, [groupedModels, isAgentChat]);

  const handleOpenChange = (newOpen: boolean) => {
    if (locked) return;
    setOpen(newOpen);
    if (newOpen && !selectedProvider && Object.keys(filteredModels).length > 0) {
      setSelectedProvider(filteredModels[Object.keys(filteredModels)[0]]);
      setMobileView('providers');
    }
  };

  const selectedModel = useMemo(() => {
    if (!value) return null;
    if (groupedModels[value.provider]) {
      const model = groupedModels[value.provider].models.find((m) => m.model === value.model);
      if (model) {
        return {
          model: model.model,
          label: model.label,
          provider: value.provider,
          providerLabel: groupedModels[value.provider].label,
          region: model.region,
        };
      }
    }
    return null;
  }, [value, groupedModels]);

  const selectedProviderGroup = useMemo(() => {
    if (!selectedProvider) return null;
    return groupedModels[selectedProvider.provider];
  }, [groupedModels, selectedProvider]);

  const handleSelect = (model: SelectModelType) => {
    onChange?.(model);
    setOpen(false);
    setSelectedProvider(null);
    setMobileView('providers');
  };

  const handleProviderSelect = (group: GroupModel) => {
    setSelectedProvider(group);
    setMobileView('models');
  };

  if (isLoading) {
    return (
      <Button
        variant="outline"
        disabled
        className="w-[220px] h-10 justify-between bg-card/50 border rounded-xl px-3"
      >
        <div className="flex items-center gap-2.5">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading models...</span>
        </div>
      </Button>
    );
  }

  if (hasError) {
    return (
      <Button
        variant="outline"
        disabled
        className="w-[220px] h-10 justify-between bg-card/50 border-border rounded-xl px-3"
      >
        <span className="text-sm text-muted-foreground">No models available</span>
      </Button>
    );
  }

  const providerConfig = getProviderConfig(selectedModel?.provider || '');
  const ProviderIcon = providerConfig.icon;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={locked}
          className={cn(
            'w-[220px] h-11 justify-between bg-card/50 border-border rounded-xl px-3 group',
            !locked && 'hover:bg-card',
            locked && 'cursor-not-allowed opacity-70'
          )}
        >
          <div className="flex items-center gap-2.5 flex-1 min-w-0">
            <div
              className={cn(
                'p-1.5 rounded-lg flex-shrink-0',
                providerConfig.bgColor,
                'group-hover:scale-110'
              )}
            >
              <ProviderIcon className={cn('h-3.5 w-3.5', providerConfig.color)} />
            </div>
            <div className="flex flex-col items-start min-w-0 flex-1">
              <div className="flex items-center gap-2 truncate w-full">
                <span className="text-sm font-medium truncate text-left">
                  {selectedModel?.label || t('SELECT_MODEL')}
                </span>
                <RegionLogo region={selectedModel?.region} />
              </div>
              <span className="text-xs text-muted-foreground truncate w-full text-left">
                {selectedModel?.providerLabel || formatProviderName(selectedModel?.provider || '')}
              </span>
            </div>
          </div>

          <ChevronDown className={cn('h-4 w-4 shrink-0 opacity-50', open && 'rotate-180')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[95vw] sm:w-[420px] lg:w-[480px] p-0 rounded-2xl border-border/50"
        align="start"
      >
        <div className="flex h-[320px] sm:h-[300px]">
          <div
            className={cn(
              'w-full sm:w-[200px] border-r border-border/30 flex flex-col bg-muted/20',
              mobileView === 'models' && 'hidden sm:flex'
            )}
          >
            <div className="px-3 py-2.5 border-b border-border/30 bg-muted/40 backdrop-blur-sm">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Providers
              </p>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="p-2 space-y-1">
                {Object.values(filteredModels).length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No providers available
                  </div>
                ) : (
                  Object.values(filteredModels).map((group) => {
                    const groupConfig = getProviderConfig(group.provider);
                    const GroupIcon = groupConfig.icon;
                    const isProviderSelected = selectedProvider?.provider === group.provider;

                    return (
                      <button
                        key={group.provider}
                        onClick={() => handleProviderSelect(group)}
                        className={cn(
                          'w-full flex items-center gap-2.5 px-2.5 py-2 text-left rounded-xl group/provider relative',
                          isProviderSelected
                            ? 'bg-primary/15 border-b border-border'
                            : 'hover:bg-accent/50 border-2 border-transparent'
                        )}
                      >
                        <div
                          className={cn(
                            'p-1.5 rounded-lg flex-shrink-0',
                            groupConfig.bgColor,
                            !isProviderSelected && 'group-hover/provider:scale-110'
                          )}
                        >
                          <GroupIcon className={cn('h-3.5 w-3.5', groupConfig.color)} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={cn(
                              'font-medium text-sm truncate',
                              isProviderSelected && 'text-primary'
                            )}
                          >
                            {group.label || formatProviderName(group.provider)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {group.models.length} {group.models.length === 1 ? 'model' : 'models'}
                          </p>
                        </div>
                        {isProviderSelected && (
                          <div className="w-1 h-8 bg-primary rounded-l-full absolute right-0" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div
            className={cn(
              'flex-1 flex flex-col min-w-0',
              mobileView === 'providers' && 'hidden sm:flex'
            )}
          >
            {selectedProviderGroup ? (
              <>
                <div className="px-3 sm:px-4 py-2.5 border-b border-border/30 bg-muted/20 backdrop-blur-sm flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="sm:hidden h-7 w-7 p-0 rounded-lg"
                    onClick={() => setMobileView('providers')}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider truncate flex-1">
                    {selectedProviderGroup.label}{' '}
                    {selectedProviderGroup.isAgents ? 'Agents' : 'Models'} (
                    {selectedProviderGroup.models.length})
                  </p>
                </div>

                <div className="flex-1 overflow-y-auto p-2 sm:p-3">
                  <div className="flex flex-row flex-wrap gap-2">
                    {selectedProviderGroup.models.map((model) => {
                      const isSelected = value?.model === model.model;
                      return (
                        <button
                          key={model.model}
                          onClick={() =>
                            handleSelect({
                              isBlocksModels: selectedProviderGroup.isBlocksModels,
                              provider: selectedProviderGroup.provider,
                              model: model.model,
                              widget_id: model.widget_id,
                              agentName: model.label,
                            })
                          }
                          className={cn(
                            'group/model flex flex-col gap-2 p-2.5 sm:p-3 rounded-xl text-left border-2 relative overflow-hidden w-fit',
                            isSelected
                              ? 'bg-primary/10 border-primary'
                              : 'bg-card/50 border-border/40 hover:bg-accent/50 hover:border-primary'
                          )}
                        >
                          {!isSelected && (
                            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover/model:opacity-100 transition-opacity duration-300" />
                          )}

                          <div className="flex items-start justify-between gap-2 relative z-10">
                            <div className="flex items-center gap-2 truncate flex-1">
                              <p
                                className={cn(
                                  'font-medium text-sm truncate',
                                  isSelected && 'text-primary'
                                )}
                                title={model.label || model.model}
                              >
                                {model.label || model.model}
                              </p>
                              <RegionLogo region={model.region} />
                            </div>
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary flex-shrink-0 animate-in zoom-in-50" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">Select a provider</p>
                  <p className="text-xs text-muted-foreground/60">
                    Choose from the {Object.keys(filteredModels).length} providers available
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
